import { ReactNode } from "react";

// TODO: temporary absolute prefix pointing at the msp-landing-config data
// host directly; swap back to a relative "/data/..." path once this app's
// own deployment proxies /data/ to that host.
const IMG = "/data/crystal";

// ============================================================
// Comparison tables
// ============================================================

export interface IComparisonColumn {
  key: string;
  label: string;
  logo: string;
}

export interface IComparisonRow {
  label: string;
  values: Record<string, ReactNode>;
}

export const goldPlusVsCrystalColumns: IComparisonColumn[] = [
  { key: "gold_plus", label: "Gold Plus", logo: `${IMG}/gold-lg.png` },
  { key: "crystal_se", label: "Crystal SE", logo: `${IMG}/crystal-lg.png` },
];

export const goldPlusVsCrystalRows: IComparisonRow[] = [
  {
    label: "Firewalla Type",
    values: {
      gold_plus: "Hardware + Software",
      crystal_se:
        "Software Only\n\nBare-metal or virtual machine deployment.\n\nMust boot from USB or an alternate flash drive.",
    },
  },
  {
    label: "Included Firewalla MSP",
    values: {
      gold_plus: "Firewalla MSP Lite",
      crystal_se: "Firewalla MSP Basic",
    },
  },
  {
    label: "Network Size",
    values: {
      gold_plus: "Limited by hardware",
      crystal_se: "100 devices (soft limit*)",
    },
  },
  {
    label: "Ports",
    values: {
      gold_plus: "4 ports",
      crystal_se:
        "Requires: 2 ports or more\n\n\n1 WAN port, 1 LAN port (additional LAN ports bridged together)\n\n\nEthernet Interfaces: Intel, Realtek, AQC, etc. (most common Ethernet interfaces)",
    },
  },
  {
    label: "Speed",
    values: { gold_plus: "> 5 Gbps", crystal_se: "Depends on hardware" },
  },
  {
    label: "Memory",
    values: {
      gold_plus: "4 GB",
      crystal_se: "Depends on hardware\nRequires: 4 GB or more",
    },
  },
  {
    label: "Storage",
    values: {
      gold_plus: "32 GB",
      crystal_se: "Depends on hardware\nRequires: 32 GB or more",
    },
  },
  {
    label: "CPU",
    values: {
      gold_plus: "64 Bit Intel 9th Gen",
      crystal_se:
        "Depends on hardware\nRequires: x86\nRequires: 2 CPU cores or more",
    },
  },
  {
    label: "Multi-WAN",
    values: { gold_plus: "Yes", crystal_se: "No" },
  },
  {
    label: "LAG",
    values: { gold_plus: "Yes", crystal_se: "No" },
  },
  {
    label: "Wi-Fi SD Support",
    values: { gold_plus: "Yes", crystal_se: "No" },
  },
  {
    label: "AP7/Switch Support",
    values: { gold_plus: "Yes", crystal_se: "Yes" },
  },
  {
    label: "Site to Site VPN",
    values: { gold_plus: "10x Connections", crystal_se: "10x Connections" },
  },
  {
    label: "Geo-IP Filtering",
    values: {
      gold_plus: "No license-imposed limit",
      crystal_se: "No license-imposed limit",
    },
  },
  {
    label: "VLANs",
    values: {
      gold_plus: "No license-imposed limit",
      crystal_se: "No license-imposed limit",
    },
  },
  {
    label: "Network Segmentation",
    values: { gold_plus: "Yes", crystal_se: "VLANs Only" },
  },
  {
    label: "Bridge Mode",
    values: { gold_plus: "Yes", crystal_se: "Yes" },
  },
  {
    label: "Router Mode",
    values: { gold_plus: "Yes", crystal_se: "Yes" },
  },
];

export const msp30DaysVsBasicColumns: IComparisonColumn[] = [
  { key: "msp_30days", label: "MSP 30-Days Seat", logo: "" },
  { key: "msp_basic", label: "MSP Basic Seat", logo: "" },
];

const CHECK = "✓";

export const msp30DaysVsBasicRows: IComparisonRow[] = [
  {
    label: "Flows Storage",
    values: { msp_30days: "30 days (cloud)", msp_basic: "24 hours (local)" },
  },
  {
    label: "Alarms Storage",
    values: { msp_30days: "30 days (cloud)", msp_basic: "30 days (cloud)" },
  },
  {
    label: "Reports",
    values: {
      msp_30days: "Flow Reports, Alarm Reports",
      msp_basic: "Alarm Reports",
    },
  },
  { label: "VPN Mesh", values: { msp_30days: CHECK, msp_basic: CHECK } },
  {
    label: "Flows & Alarms Search",
    values: {
      msp_30days: "Advanced (Flows, Alarms)",
      msp_basic: "Basic (Flows) + Advanced (Alarms)",
    },
  },
  {
    label: "Active Protect",
    values: {
      msp_30days: "Default + MSP (Alarm Optimizer, Advanced Behavioral Alarm)",
      msp_basic: "Default + MSP (Alarm Optimizer)",
    },
  },
  {
    label: "Custom Target Lists",
    values: {
      msp_30days: "Up to 100 lists (2000 targets per list)",
      msp_basic: "Up to 100 lists (2000 targets per list)",
    },
  },
  {
    label: "Import Target List",
    values: { msp_30days: CHECK, msp_basic: CHECK },
  },
  {
    label: "Email Notifications",
    values: { msp_30days: CHECK, msp_basic: CHECK },
  },
  {
    label: "Events & User Activites",
    values: { msp_30days: CHECK, msp_basic: CHECK },
  },
  {
    label: "Events Storage",
    values: { msp_30days: "30 days (cloud)", msp_basic: "30 days (cloud)" },
  },
  {
    label: "Internet Speed Test & Quality Test Results",
    values: { msp_30days: "30 days (cloud)", msp_basic: "30 days (cloud)" },
  },
];

// ============================================================
// Hardware requirements grid
// ============================================================

export interface IHardwareRequirement {
  icon: string;
  title: string;
  // Rendered as one <p> per line, rather than a single string, since a few
  // items pair a spec with a parenthetical note on its own line (e.g.
  // "x86 / x86-64" + "2 CPU cores or more").
  description: string[];
}

export const hardwareRequirements: IHardwareRequirement[] = [
  {
    icon: `${IMG}/hardware-icon-deployment.png`,
    title: "Deployment",
    description: ["Bare Metal or Virtual Machine"],
  },
  {
    icon: `${IMG}/hardware-icon-processor.png`,
    title: "Processor",
    description: ["x86 / x86-64", "2 CPU cores or more"],
  },
  {
    icon: `${IMG}/hardware-icon-memory.png`,
    title: "Memory",
    description: ["4 GB or more"],
  },
  {
    icon: `${IMG}/hardware-icon-ports.png`,
    title: "Ports",
    description: ["Up to 2 Ports", "(additional LAN ports bridged together)"],
  },
  {
    icon: `${IMG}/hardware-icon-ethernet.png`,
    title: "Ethernet Interfaces",
    description: ["Intel, Realtek, AQC, etc.", "(most common Ethernet interfaces)"],
  },
  {
    icon: `${IMG}/hardware-icon-storage.png`,
    title: "Storage",
    description: ["32 GB or more"],
  },
  {
    icon: `${IMG}/hardware-icon-other.png`,
    title: "Other Requirements",
    description: ["Must boot from USB or alternate flash drive"],
  },
];

// ============================================================
// Feature slideshow (5 switchable categories, per Wusiqi77's spec on
// https://github.com/firewalla/firecommit/issues/9484)
// ============================================================

// Each slide is its own page (the category is a pager, not a continuous
// scroll strip): its own heading/description, pinned to the top-left corner
// of that page, with the visual(s) filling the rest of the page.
export interface ISlideshowSlide {
  heading: string;
  subheading?: string;
  body?: string;
  bullets?: string[];
  images: string[];
  alt: string;
  // Layout override; default is "side-by-side" (text-left, images-right).
  // "top-bottom": the whole slide stacks - text on top, image(s) below,
  // full width. Used when a single wide diagram has no room in a side
  // column.
  // "text-stack-plus-image": the first image renders stacked directly
  // under the text as one left-hand column, and any remaining images
  // render in a separate column to the right of that.
  layout?: "side-by-side" | "top-bottom" | "text-stack-plus-image";
  imageHeight?: number;
  // Narrows the text column (default 522px) to give the image column more
  // of the card's fixed total width - needed when imageHeight is set on an
  // image whose aspect ratio needs more width than the default split
  // allows to reach that height without letterboxing.
  textWidth?: number;
}

export interface ISlideshowTab {
  key: string;
  label: string;
  // A small icon only - the label above is rendered as real text next to it
  // (an earlier flattened icon+text image distorted on narrow viewports:
  // its fixed height couldn't shrink in step with max-width capping its
  // width).
  titleIcon: string;
  kind: "scroll";
  slides: ISlideshowSlide[];
}

export interface IRouterBridgeColumn {
  titleIcon: string;
  label: string;
  body?: string;
  image: string;
  imageCaption?: string;
  bodyAfterImage?: string;
  bullets?: string[];
  alt: string;
}

export interface IRouterBridgeTab {
  key: "router_bridge";
  label: string;
  kind: "special-dual";
  columns: IRouterBridgeColumn[];
}

// Slide order and grouping below matches the file naming the design team
// provided (e.g. "CyberSecurity#3", "BuildNetwork#4-1" / "BuildNetwork#4-2"
// sharing one slide) - see the discussion on
// https://github.com/firewalla/firecommit/issues/9484.
export const slideshowTabs: (ISlideshowTab | IRouterBridgeTab)[] = [
  {
    key: "cyber_security",
    label: "Cyber Security",
    titleIcon: `${IMG}/security.svg`,
    kind: "scroll",
    slides: [
      {
        heading: "How Does Firewalla Cyber Security Work?",
        bullets: [
          "Deep Insight: Makes sense of all network traffic and flows.",
          "Active Protect: Intrusion detection (IDS) and prevention (IPS) to detect and block potential attacks on your network.",
          "Behavior analytics: Analyzes devices’ intent through actions on the network.",
          "Control: Apply sophisticated rules to control traffic flows.",
        ],
        images: [`${IMG}/slide-cybersecurity-1-architecture.gif`],
        alt: "Firewalla cyber security architecture diagram",
      },
      {
        heading: "Firewalla sends automatic alerts and protects your devices from cyber threats.",
        bullets: [
          "Detect, prevent and block active cyberattacks and intrusions",
          "Monitor for abnormal trends through intelligent behavior analytics",
          "Actively block malicious sites in real-time",
          "Continuously scan your network for security vulnerabilities and risks",
        ],
        images: [`${IMG}/slide-cybersecurity-2-blocked.gif`],
        alt: "Suspicious device automatically blocked",
      },
      {
        heading: "Region Blocking, Advanced Threat Filtering, & Bypass Prevention",
        body: "Use GEO-IP Filtering to block entire countries from your network, protect your network from domains used in phishing and scams, and prevent devices from bypassing your policies.",
        images: [`${IMG}/slide-cybersecurity-3-region-blocking.png`],
        alt: "Region blocking and bypass prevention",
      },
      {
        heading: "Zero Trust Network Architecture",
        body: "**Zero Trust** network architecture protects your network by trusting no one by default and verifying everyone, everywhere.",
        bullets: [
          "Lock down devices and restrict their behavior on the network.",
          "New devices connecting to the network will need approval.",
          "Block traffic to and from network segments, allowing sites or services as needed.",
        ],
        images: [`${IMG}/slide-cybersecurity-8-zero-trust.png`],
        alt: "Zero Trust network architecture",
        layout: "top-bottom",
      },
      {
        heading: "Device Active Protect: Lockdown",
        body: "Dynamically segment IoT devices and limit their connections to their own servers. Protect your network from intrusion and from attacking others.",
        images: [`${IMG}/slide-cybersecurity-4-active-protect.gif`],
        alt: "Device Active Protect and Firewalla AI",
        imageHeight: 573,
      },
      {
        heading: "New Device Quarantine",
        body: "Protect your business network by automatically placing new/unknown devices in a Quarantine group with more restricted rules.",
        images: [`${IMG}/slide-cybersecurity-6-quarantine.png`],
        alt: "New device quarantine, alarms and Firewalla AI",
        imageHeight: 573,
        textWidth: 340,
      },
      {
        heading: "Network Segmentation",
        body: "Create secondary firewalls within your network through \"network segmentation.\" Divide your network into regions with dynamic and static policies/rules.",
        bullets: [
          "Create separate segments for IoT and employees with custom policies.",
          "Isolate critical devices into their own network.",
          "Create a secure guest network and manage activites in real-time.",
        ],
        images: [`${IMG}/slide-cybersecurity-5-segmentation.gif`],
        alt: "Network segmentation across IoT, guest and employee networks",
      },
      {
        heading: "Scan",
        body: "Identify security risks across your business network, including open ports and weak passwords.",
        images: [`${IMG}/slide-cybersecurity-7-scan.png`],
        alt: "Scan for open ports and system vulnerabilities",
        imageHeight: 573,
      },
    ],
  },
  {
    key: "privacy_protection",
    label: "Privacy Protection",
    titleIcon: `${IMG}/network.svg`,
    kind: "scroll",
    slides: [
      {
        heading: "Make Your Privacy a Priority with VPN",
        body: "Firewalla VPN Server runs inside your business network without charging a monthly fee.",
        bullets: [
          "Protects privacy when using public Wi-Fi",
          "No bandwidth throttling",
          "Bypass internet filtering and monitoring when traveling",
          "Supports OpenVPN, WireGuard, and AmneziaWG",
        ],
        images: [`${IMG}/slide-privacy-1-vpn-server.png`],
        alt: "VPN server with OpenVPN, WireGuard and AmneziaWG",
        imageHeight: 573,
      },
      {
        heading: "VPN Client: Securely Connect to Any Device",
        body: "Transparently connect any device or devices in your network to OpenVPN, WireGuard, and AnyConnect-based services, or connect multiple business sites to access local devices securely.",
        images: [`${IMG}/slide-privacy-2-vpn-client.png`],
        alt: "VPN client connection types",
        imageHeight: 573,
      },
      {
        heading: "Say Goodbye to Annoying Ads",
        body: "Website ads and trackers have been frustrating internet users for years. That's why Firewalla's built-in ad blocker makes sure you don't see ads and the ads don't see you.",
        images: [`${IMG}/slide-privacy-3-adblock.png`],
        alt: "Ad Block on vs off",
        imageHeight: 573,
      },
      {
        heading: "DNS Over HTTPS",
        body: "Traditionally, DNS requests are sent in plain text between the client and the DNS server. DNS over HTTPS (DoH) sends DNS requests encrypted over HTTPS. It makes DNS exchange more secure and protects business privacy.",
        images: [`${IMG}/slide-privacy-4-doh.gif`],
        alt: "DNS over HTTPS request flow",
        layout: "top-bottom",
      },
      {
        heading: "Unbound",
        body: "Unbound is a validating, recursive, caching DNS resolver, installed locally on Firewalla, which helps increase your business's online privacy and security",
        images: [`${IMG}/slide-privacy-5-unbound.png`],
        alt: "Unbound DNS resolver diagram",
        layout: "top-bottom",
      },
      {
        heading: "NTP Intercept: Securely Sync Time",
        body: "Intercept NTP requests and process them locally on the Firewalla, reducing your network's risk exposure and saving bandwidth.",
        images: [`${IMG}/slide-privacy-6-ntp.gif`],
        alt: "NTP intercept request flow",
        layout: "top-bottom",
      },
    ],
  },
  {
    key: "better_network",
    label: "Build a Better Network",
    titleIcon: `${IMG}/topology.svg`,
    kind: "scroll",
    slides: [
      {
        heading: "Powerful Rules Engine",
        body: "Firewalla puts the power in your hands and gives you full control of your business network. Block domains, IP addresses, cut off access to devices, remove ads, or block sites by category, such as Social, Video, and more.",
        images: [`${IMG}/slide-network-1-rules.png`],
        alt: "Rules matching and per-network rule list",
        imageHeight: 573,
      },
      {
        heading: "Regulate Network Traffic The Smart Way",
        subheading: "Advanced Smart Queue + Rate Limit",
        body: "Advanced Smart Queue prioritizes traffic and reduces delays (buffer bloat) when your network is heavily loaded. Control bandwidth usage by rate-limiting devices or activities.",
        images: [
          `${IMG}/slide-network-2a-smart-queue-diagram.gif`,
          `${IMG}/slide-network-2b-smart-queue-app.png`,
        ],
        alt: "Smart Queue regulating traffic",
        layout: "text-stack-plus-image",
        imageHeight: 573,
      },
      {
        heading: "Smart Routing",
        subheading: "Policy & Content-Based Routing (PBR)",
        body: "Selectively route traffic based on a set of rules or the content type. Send all video traffic to VPN Client or critical business sites to a different WAN interface.",
        images: [`${IMG}/slide-network-3-routes.png`],
        alt: "Policy-based routes",
        imageHeight: 573,
      },
      {
        heading: "Network Performance and Quality Monitoring",
        body: "A better network also enables a much better experience. Firewalla provides tools to help you monitor and improve performance, including Wi-Fi, LAN, and WAN (such as Internet speed, latency, and packet loss).",
        images: [
          `${IMG}/slide-network-4a-health-cards.png`,
          `${IMG}/slide-network-4b-health-app.png`,
        ],
        alt: "Network health and internet speed/quality",
        layout: "text-stack-plus-image",
        imageHeight: 573,
      },
      {
        heading: "Firewalla AI Assistant",
        body: "Firewalla simplifies your experience and helps you decide how to control and protect your network with its AI-powered feature. Ask AI for help with:",
        bullets: [
          "Identifying unknown devices",
          "Understanding alarms",
          "Troubleshooting your network",
          "Learning about unknown domains",
        ],
        images: [`${IMG}/slide-network-5-health-ai.gif`],
        alt: "Network health powered by AI",
      },
    ],
  },
  {
    key: "content_control",
    label: "Content Control",
    titleIcon: `${IMG}/account.svg`,
    kind: "scroll",
    slides: [
      {
        heading: '',
        body: "With a simple tap, restrict internet access, block popular apps and categories, schedule rules, monitor online activity with Alarms, and set all rules on a schedule.",
        images: [`${IMG}/slide-content-1-control-demo.gif`],
        alt: "Content control device list",
      },
      {
        heading: "Users",
        body: "Track and limit app usage for a set of devices and monitor when certain apps are used.",
        images: [`${IMG}/slide-content-2-user-rules.png`],
        alt: "Per-user device rules",
        imageHeight: 573,
      },
    ],
  },
  {
    key: "router_bridge",
    label: "Router Mode & Bridge Mode",
    kind: "special-dual",
    columns: [
      {
        titleIcon: `${IMG}/router.svg`,
        label: "Router Mode",
        body: "Firewalla Crystal can operate in Router Mode, giving you the option to use the Firewalla as your main router.",
        image: `${IMG}/slide-router-mode-diagram.png`,
        bodyAfterImage: "Firewalla Crystal is a software-based router, managed by the same intuitive Firewalla app.",
        bullets: ["VLAN Support", "Static Routing", "DHCP", "PPPoE", "Static IP"],
        alt: "Router mode network diagram",
      },
      {
        titleIcon: `${IMG}/bridge.svg`,
        label: "Bridge Mode",
        body: "Bridge two networks together by inserting Firewalla Crystal behind your existing router without additional configurations, and have Firewalla transparently secure your business network.",
        image: `${IMG}/slide-bridge-mode-diagram.png`,
        alt: "Bridge mode network diagram, before and after",
      },
    ],
  },
];
